import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "./utils/cn";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

interface CommonButtonProps {
  /**
   * Semantic role of the button:
   * - `primary`   filled brand button — the main action on a surface
   * - `secondary` outlined brand button — secondary action
   * - `danger`    filled red button — destructive action
   * - `ghost`     low-emphasis bordered button — cancel / tertiary
   */
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  /** Icon rendered at the start (flips with RTL). */
  prefixIcon?: ReactNode;
  /** Icon rendered at the end (flips with RTL). */
  suffixIcon?: ReactNode;
}

export type ButtonProps = CommonButtonProps &
  (
    | ({ as?: "button" } & ButtonHTMLAttributes<HTMLButtonElement>)
    | ({ as: "a" } & AnchorHTMLAttributes<HTMLAnchorElement>)
  );

const base =
  "relative inline-flex select-none items-center justify-center gap-2 rounded-pill font-medium " +
  "transition-[transform,background-color,box-shadow,filter,border-color] duration-200 ease-out-soft " +
  "active:scale-[0.97] focus-visible:outline-none focus-visible:ring focus-visible:ring-offset-0 " +
  "disabled:pointer-events-none disabled:opacity-60 aria-disabled:pointer-events-none aria-disabled:opacity-60";

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-secondary text-white shadow-e1 hover:shadow-e2 hover:-translate-y-px",
  secondary:
    "border border-secondary bg-surface text-secondary shadow-e1 hover:bg-secondary/10 hover:shadow-e2",
  danger:
    "bg-danger text-white shadow-e1 hover:shadow-e2 hover:-translate-y-px",
  ghost: "text-menu hover:bg-surface-subtle",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-control-sm px-4 text-sm",
  md: "h-control px-6 text-base",
  lg: "h-control-lg px-8 text-base",
};

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (props, ref) => {
    const {
      variant = "primary",
      size = "md",
      isLoading = false,
      fullWidth = false,
      prefixIcon,
      suffixIcon,
      className,
      children,
      disabled,
      ...rest
    } = props as CommonButtonProps & {
      className?: string;
      children?: ReactNode;
      disabled?: boolean;
      as?: "button" | "a";
    } & Record<string, unknown>;

    const content = (
      <>
        {isLoading && (
          <span
            aria-hidden="true"
            className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        )}
        {!isLoading && prefixIcon}
        {children}
        {suffixIcon}
      </>
    );

    const classes = cn(
      base,
      variantClasses[variant],
      sizeClasses[size],
      fullWidth && "w-full",
      className
    );

    if (props.as === "a") {
      const { as: _as, ...anchorRest } =
        rest as AnchorHTMLAttributes<HTMLAnchorElement> & {
          as?: string;
        };
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={classes}
          aria-disabled={disabled || isLoading || undefined}
          {...anchorRest}
        >
          {content}
        </a>
      );
    }

    const {
      as: _as,
      type,
      ...buttonRest
    } = rest as ButtonHTMLAttributes<HTMLButtonElement> & {
      as?: string;
    };
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={(type as "button" | "submit" | "reset") ?? "button"}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        className={classes}
        {...buttonRest}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
